import type { TrialView } from '@/viewmodel/features/entitlements';
import type { TrialBannerTone, TrialPhase } from '@/viewmodel/features/quota/types';
import type { TFunction } from 'i18next';

export interface TrialBannerText {
  readonly tone: TrialBannerTone;
  readonly title: string;
  readonly text: string;
}

export interface TrialClockText {
  readonly tone: TrialBannerTone;
  readonly label: string;
  readonly tip: string;
}

export function trialDate(endsAt: Date | null, t: TFunction): string | null {
  return endsAt ? t('trial.date', { date: endsAt }) : null;
}

export function trialPhase(trial: TrialView): TrialPhase {
  if (trial.expired) return 'expired';
  return trial.urgent ? 'urgent' : 'active';
}

function trialTone(trial: TrialView): TrialBannerTone {
  if (trial.expired) return 'danger';
  return trial.urgent ? 'warning' : 'info';
}

function trialBody(trial: TrialView, t: TFunction): string {
  const date = trialDate(trial.endsAt, t);
  if (trial.expired) {
    return date ? t('trial.banner.expiredOn', { date }) : t('trial.banner.expired');
  }
  return date ? t('trial.banner.active', { date }) : t('trial.banner.activeUndated');
}

export function trialBannerText(trial: TrialView, t: TFunction): TrialBannerText {
  if (trial.expired) {
    return { tone: 'danger', title: t('trial.banner.expiredTitle'), text: trialBody(trial, t) };
  }

  return {
    tone: trialTone(trial),
    title:
      trial.daysLeft === 0
        ? t('trial.banner.lastDayTitle')
        : t('trial.banner.activeTitle', { count: trial.daysLeft }),
    text: trialBody(trial, t),
  };
}

export function trialClockText(trial: TrialView, t: TFunction): TrialClockText {
  const label = (() => {
    if (trial.expired) return t('trial.clock.ended');
    if (trial.daysLeft === 0) return t('trial.plan.lastDay');
    return t('trial.plan.daysLeft', { count: trial.daysLeft });
  })();

  return { tone: trialTone(trial), label, tip: trialBody(trial, t) };
}

export function trialLockText(t: TFunction): {
  title: string;
  description: string;
  summary: string;
} {
  return {
    title: t('trial.expired.lockTitle'),
    description: t('trial.expired.lockDescription'),
    summary: t('trial.expired.emptyState'),
  };
}
